import React, { useState, useEffect } from 'react';
import { usePersistedState } from '@/hooks/usePersistedState';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, AlertCircle, CheckCircle, Loader2, ChevronRight,
  Users, DollarSign, BarChart3, X, GitCompare, TrendingUp, TrendingDown, Minus
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';

const API = process.env.REACT_APP_BACKEND_URL;

function HealthDot({ score, size = 10 }) {
  const color = score >= 80 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444';
  return <span style={{ width: size, height: size, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />;
}

function HealthLabel({ score }) {
  const color = score >= 80 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444';
  const label = score >= 80 ? 'Healthy' : score >= 50 ? 'Attention' : 'Critical';
  return <Badge className="text-[8px] font-bold" style={{ backgroundColor: `${color}15`, color, border: `1px solid ${color}35` }}>{score} — {label}</Badge>;
}

function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="px-5 py-3.5 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#E63946]/20 via-[#F97316]/15 to-transparent relative overflow-hidden rounded-t-xl">
      <div className="absolute inset-0 bg-gradient-to-r from-[#E63946]/10 to-transparent opacity-50" />
      <div className="relative flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 border border-[#E63946]/40">
          <Icon size={18} className="text-white" />
        </div>
        <div>
          <h3 className="text-base font-bold text-white">{title}</h3>
          <p className="text-[10px] text-[#9CA3AF]">{subtitle}</p>
        </div>
      </div>
    </div>
  );
}

function SectionCard({ children }) {
  return <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">{children}</div>;
}

function MiniSparkline({ data, color = '#8b5cf6', width = 60, height = 20 }) {
  if (!data || data.length === 0 || data.every(v => v === 0)) return <span className="text-[9px] text-[hsl(var(--text-muted))]">—</span>;
  const max = Math.max(...data, 0.01);
  const points = data.map((v, i) => `${(i / (data.length - 1)) * width},${height - (v / max) * (height - 2)}`).join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function DeltaBadge({ cur, prev, higherBetter }) {
  const hasPrev = prev !== undefined && prev !== null;
  if (!hasPrev || (cur === 0 && prev === 0)) {
    return <span className="text-[9px] text-[hsl(var(--text-muted))]">—</span>;
  }
  const d = prev === 0 ? (cur > 0 ? 100 : 0) : ((cur - prev) / prev) * 100;
  const positive = higherBetter ? d >= 0 : d <= 0;
  const isFlat = Math.abs(d) < 0.5;
  const color = isFlat ? '#9ca3af' : (positive ? '#10b981' : '#ef4444');
  const Icon = isFlat ? Minus : (d >= 0 ? TrendingUp : TrendingDown);
  return (
    <span className="inline-flex items-center gap-0.5 text-[9px] font-bold" style={{ color }}>
      <Icon size={9} />
      {Math.abs(d).toFixed(0)}%
    </span>
  );
}

export default function WarRoomHQ() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortCol, setSortCol] = usePersistedState('war-room:sortCol', 'health');
  const [sortAsc, setSortAsc] = usePersistedState('war-room:sortAsc', true);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [compareSet, setCompareSet] = useState(() => new Set());
  const [showCompare, setShowCompare] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  // Period picker for compare dialog (defaults to current month; loaded on mount)
  const [comparePeriod, setComparePeriod] = useState(null);
  const [compareData, setCompareData] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async (period = null) => {
    if (!period) setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = period ? `${API}/api/war-room-hq?period=${period}` : `${API}/api/war-room-hq`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const json = await res.json();
        if (period) {
          setCompareData(json);
        } else {
          setData(json);
        }
      }
    } catch { /* ignore */ } finally {
      if (!period) setLoading(false);
    }
  };

  // Build period options (current + 5 past months)
  const periodOptions = React.useMemo(() => {
    const opts = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleString('en-MY', { month: 'short', year: 'numeric' });
      opts.push({ key, label });
    }
    return opts;
  }, []);

  // When user changes comparePeriod, refetch (skip refetch if it's the current period)
  useEffect(() => {
    if (!showCompare || !comparePeriod) return;
    const currentKey = periodOptions[0]?.key;
    if (comparePeriod === currentKey) {
      setCompareData(null); // use main `data`
      return;
    }
    setCompareLoading(true);
    fetchData(comparePeriod).finally(() => setCompareLoading(false));
  }, [comparePeriod, showCompare, periodOptions]);

  if (loading) return (
    <div className="flex items-center justify-center py-20 gap-2">
      <Loader2 size={18} className="animate-spin text-[#E63946]" />
      <span className="text-sm text-[hsl(var(--text-muted))]">Loading War Room...</span>
    </div>
  );

  if (!data) return null;

  const { channels, all_issues } = data;

  // Filter: only channels with data
  const activeChannels = channels.filter(c => c.has_data);

  const sorted = [...activeChannels].sort((a, b) => {
    const av = a[sortCol] ?? 0;
    const bv = b[sortCol] ?? 0;
    return sortAsc ? av - bv : bv - av;
  });

  const toggleSort = (col) => {
    if (sortCol === col) setSortAsc(!sortAsc);
    else { setSortCol(col); setSortAsc(col === 'health'); }
  };

  const SortHeader = ({ col, label }) => (
    <th className="py-2.5 px-3 text-left text-[9px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider cursor-pointer hover:text-white transition-colors select-none" onClick={() => toggleSort(col)}>
      <span className="flex items-center gap-1">
        {label}
        {sortCol === col && (sortAsc ? <ChevronRight size={10} className="rotate-90" /> : <ChevronRight size={10} className="-rotate-90" />)}
      </span>
    </th>
  );

  const criticalCount = all_issues.filter(i => i.severity === 'critical').length;
  const warningCount = all_issues.filter(i => i.severity === 'warning').length;
  const healthyCount = activeChannels.filter(c => c.health >= 80).length;
  const attentionCount = activeChannels.filter(c => c.health >= 50 && c.health < 80).length;

  // Format helpers for MTD-vs-Prev comparison grid.
  const fmtMoney = (v) => v > 0 ? `RM${Number(v).toLocaleString('en-MY', { maximumFractionDigits: 0 })}` : '—';
  const fmtUSD = (v) => v > 0 ? `$${Number(v).toLocaleString('en-MY', { maximumFractionDigits: 0 })}` : '—';
  const fmtCPL = (v) => v > 0 ? `$${Number(v).toFixed(1)}` : '—';
  const fmtNum = (v) => v > 0 ? Number(v).toLocaleString() : '—';
  const fmtPct = (v) => v > 0 ? `${v}%` : '—';
  const COMPARE_METRICS = [
    { key: 'commission', label: 'Commission', fmt: fmtMoney, higherBetter: true },
    { key: 'deposit', label: 'Deposit', fmt: fmtUSD, higherBetter: true },
    { key: 'spend', label: 'Ad Spend', fmt: fmtMoney, higherBetter: false, hint: 'lower = efficient' },
    { key: 'subs', label: 'Subs', fmt: fmtNum, higherBetter: true },
    { key: 'leads_sales', label: 'Leads', fmt: fmtNum, higherBetter: true },
    { key: 'closed', label: 'Closed', fmt: fmtNum, higherBetter: true },
    { key: 'closing_rate', label: 'Close %', fmt: fmtPct, higherBetter: true },
    { key: 'com_per_lot', label: 'Com/Lot', fmt: fmtCPL, higherBetter: true },
    { key: 'traded_lot', label: 'Lot', fmt: fmtNum, higherBetter: true },
  ];
  const prevMonthLabel = data.prev_month
    ? (() => {
        try {
          const [py, pm] = data.prev_month.split('-').map(Number);
          return new Date(py, pm - 1, 1).toLocaleString('en-MY', { month: 'short', year: 'numeric' });
        } catch { return data.prev_month; }
      })()
    : 'Prev';
  const curMonthLabel = data.month
    ? (() => {
        try {
          const [cy, cm] = data.month.split('-').map(Number);
          return new Date(cy, cm - 1, 1).toLocaleString('en-MY', { month: 'short', year: 'numeric' });
        } catch { return data.month; }
      })()
    : 'MTD';

  return (
    <div className="px-4 lg:px-6 py-5 space-y-5" data-testid="war-room-hq">
      {/* Scoreboard */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <div className="p-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))]">
          <div className="text-[9px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider mb-1">Active Channels</div>
          <div className="text-2xl font-bold text-white font-mono">{activeChannels.length}</div>
        </div>
        <div className="p-4 rounded-xl border border-[#10b981]/30 bg-[#10b981]/5">
          <div className="text-[9px] font-bold text-[#10b981] uppercase tracking-wider mb-1">Healthy</div>
          <div className="text-2xl font-bold text-[#10b981] font-mono">{healthyCount}</div>
        </div>
        <div className="p-4 rounded-xl border border-[#f59e0b]/30 bg-[#f59e0b]/5">
          <div className="text-[9px] font-bold text-[#f59e0b] uppercase tracking-wider mb-1">Needs Attention</div>
          <div className="text-2xl font-bold text-[#f59e0b] font-mono">{attentionCount}</div>
        </div>
        <div className="p-4 rounded-xl border border-[#ef4444]/30 bg-[#ef4444]/5">
          <div className="text-[9px] font-bold text-[#ef4444] uppercase tracking-wider mb-1">Critical Issues</div>
          <div className="text-2xl font-bold text-[#ef4444] font-mono">{criticalCount}</div>
        </div>
        <div className="p-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))]">
          <div className="text-[9px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider mb-1">Warnings</div>
          <div className="text-2xl font-bold text-[#f59e0b] font-mono">{warningCount}</div>
        </div>
      </div>

      {/* MTD vs Bulan Lepas — side-by-side snapshot of all channels */}
      <SectionCard>
        <SectionHeader
          icon={BarChart3}
          title="MTD vs Bulan Lepas"
          subtitle={`${curMonthLabel} (Day ${data.day}) vs ${prevMonthLabel} (Day 1–${data.day}) — % change highlights channels yang tak perform`}
        />
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]" data-testid="hq-mtd-comparison">
            <thead>
              <tr className="border-b border-[hsl(var(--border))]">
                <th className="py-2.5 px-3 text-left text-[9px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider sticky left-0 bg-[hsl(var(--surface))] z-10 min-w-[140px]">Channel</th>
                {COMPARE_METRICS.map(m => (
                  <th key={m.key} className="py-2.5 px-2 text-center text-[9px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider" title={m.hint || ''}>
                    {m.label}
                    {m.hint && <div className="text-[8px] text-[hsl(var(--text-muted))]/60 font-normal normal-case tracking-normal">{m.hint}</div>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map(ch => (
                <tr key={ch.ib_id} className="border-b border-[hsl(var(--border))]/40 hover:bg-[hsl(var(--surface-elevated))] transition-colors" data-testid={`hq-mtd-row-${ch.ib_id}`}>
                  <td className="py-2 px-3 font-semibold text-white sticky left-0 bg-[hsl(var(--surface))] z-10">
                    <span className="flex items-center gap-2">
                      <HealthDot score={ch.health} size={8} />
                      <span className="truncate max-w-[120px]" title={ch.name}>{ch.name}</span>
                    </span>
                  </td>
                  {COMPARE_METRICS.map(m => {
                    const cur = ch[m.key] ?? 0;
                    const prev = ch.prev_mtd?.[m.key] ?? 0;
                    return (
                      <td key={m.key} className="py-2 px-2 text-center align-middle">
                        <div className="font-mono font-semibold text-white text-[11px] leading-tight">{m.fmt(cur)}</div>
                        <div className="text-[9px] text-[hsl(var(--text-muted))] font-mono leading-tight">vs {m.fmt(prev)}</div>
                        <div className="mt-0.5"><DeltaBadge cur={cur} prev={prev} higherBetter={m.higherBetter} /></div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t border-[hsl(var(--border))]/50 text-[10px] text-[hsl(var(--text-muted))] flex flex-wrap items-center gap-4">
          <span className="inline-flex items-center gap-1"><TrendingUp size={10} className="text-[#10b981]" /> Improvement</span>
          <span className="inline-flex items-center gap-1"><TrendingDown size={10} className="text-[#ef4444]" /> Decline</span>
          <span className="inline-flex items-center gap-1"><Minus size={10} className="text-[#9ca3af]" /> Flat / no prev data</span>
          <span className="ml-auto italic">Warna hijau = improved (untuk cost metric = turun/lebih efficient)</span>
        </div>
      </SectionCard>

      {/* Critical Alerts — click opens popup, NOT navigate */}
      {all_issues.length > 0 && (
        <SectionCard>
          <SectionHeader icon={AlertTriangle} title="Critical Alerts" subtitle={`${all_issues.length} issues across all channels`} />
          <div className="p-4 space-y-1.5 max-h-[280px] overflow-y-auto">
            {all_issues.map((iss, i) => {
              const color = iss.severity === 'critical' ? '#ef4444' : iss.severity === 'warning' ? '#f59e0b' : '#3b82f6';
              const Icon = iss.severity === 'critical' ? AlertTriangle : AlertCircle;
              const ch = activeChannels.find(c => c.name === iss.channel);
              return (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))] hover:border-[hsl(var(--border-hover))] transition-all cursor-pointer" style={{ borderLeftWidth: 3, borderLeftColor: color }}
                  onClick={() => setSelectedIssue({ ...iss, channelData: ch })}
                  data-testid={`hq-issue-${i}`}
                >
                  <Icon size={13} style={{ color, flexShrink: 0 }} />
                  <Badge className="text-[8px] font-bold shrink-0" style={{ backgroundColor: `${color}15`, color, border: `1px solid ${color}35` }}>{iss.channel}</Badge>
                  <span className="text-[11px] text-white/80 flex-1">{iss.message}</span>
                  <span className="text-[9px] font-bold uppercase" style={{ color }}>{iss.area}</span>
                </div>
              );
            })}
          </div>
        </SectionCard>
      )}

      {/* Channel Ranking Table */}
      <SectionCard>
        <div className="flex items-center justify-between border-b border-[hsl(var(--border))]">
          <div className="flex-1">
            <SectionHeader
              icon={BarChart3}
              title="Channel Ranking"
              subtitle={compareMode ? (compareSet.size >= 2 ? `Compare mode: ${compareSet.size} selected — click "Compare Selected"` : 'Tick 2–4 channels to compare side-by-side') : 'Sortable ranking of all active channels'}
            />
          </div>
          <div className="pr-5 flex items-center gap-2">
            {compareMode && compareSet.size > 0 && (
              <button
                onClick={() => setCompareSet(new Set())}
                className="text-[10px] text-[hsl(var(--text-muted))] hover:text-white transition-all px-2 py-1 rounded"
                data-testid="btn-clear-compare-header"
              >
                Clear
              </button>
            )}
            <button
              onClick={() => {
                setCompareMode((prev) => {
                  if (prev) {
                    setCompareSet(new Set());
                    setShowCompare(false);
                  }
                  return !prev;
                });
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all border ${
                compareMode
                  ? 'bg-[#E63946] text-white border-[#E63946] hover:bg-[#d32f3f]'
                  : 'bg-transparent text-white border-[hsl(var(--border))] hover:border-[#E63946]/50'
              }`}
              data-testid="btn-toggle-compare-mode"
            >
              <GitCompare size={12} />
              {compareMode ? 'Exit Compare' : 'Compare Channels'}
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-b border-[hsl(var(--border))]">
                {compareMode && (
                  <th className="py-2.5 px-2 w-8 text-center text-[9px] font-bold text-[hsl(var(--text-muted))] uppercase" title="Select for compare">
                    <GitCompare size={12} className="inline-block text-[hsl(var(--text-muted))]" />
                  </th>
                )}
                <th className="py-2.5 px-3 text-left text-[9px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider w-[160px]">Channel</th>
                <SortHeader col="health" label="Health" />
                <SortHeader col="spend" label="Spend" />
                <SortHeader col="subs" label="Subs" />
                <SortHeader col="cps" label="CPS" />
                <SortHeader col="leads_sales" label="Leads" />
                <SortHeader col="closed" label="Closed" />
                <SortHeader col="closing_rate" label="Close %" />
                <SortHeader col="commission" label="Commission" />
                <SortHeader col="com_per_lot" label="Com/Lot" />
                <SortHeader col="deposit" label="Deposit" />
                <SortHeader col="content_score" label="Content" />
                <th className="py-2.5 px-3 text-[9px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider">7d Trend</th>
                <th className="py-2.5 px-3 text-[9px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider">Issues</th>
                <th className="py-2.5 px-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(ch => (
                <tr key={ch.ib_id} className={`border-b border-[hsl(var(--border))]/40 hover:bg-[hsl(var(--surface-elevated))] transition-colors ${compareSet.has(ch.ib_id) ? 'bg-[#E63946]/5' : ''}`} data-testid={`hq-channel-${ch.ib_id}`}>
                  {compareMode && (
                    <td className="py-2.5 px-2 text-center">
                      <Checkbox
                        checked={compareSet.has(ch.ib_id)}
                        disabled={!compareSet.has(ch.ib_id) && compareSet.size >= 4}
                        onCheckedChange={(v) => {
                          setCompareSet((prev) => {
                            const next = new Set(prev);
                            if (v) next.add(ch.ib_id); else next.delete(ch.ib_id);
                            return next;
                          });
                        }}
                        data-testid={`compare-check-${ch.ib_id}`}
                      />
                    </td>
                  )}
                  <td className="py-2.5 px-3 font-semibold text-white">
                    <span className="flex items-center gap-2 cursor-pointer" onClick={() => setSelectedIssue({ severity: 'info', area: 'Channel', message: `${ch.name} overview`, channel: ch.name, channelData: ch })}>
                      <HealthDot score={ch.health} />
                      <span className="truncate max-w-[120px]">{ch.name}</span>
                    </span>
                  </td>
                  <td className="py-2.5 px-3"><HealthLabel score={ch.health} /></td>
                  <td className="py-2.5 px-3 font-mono text-white">{ch.spend > 0 ? `RM${ch.spend.toLocaleString('en-MY', {maximumFractionDigits: 0})}` : '—'}</td>
                  <td className="py-2.5 px-3 font-mono text-white">{ch.subs || '—'}</td>
                  <td className="py-2.5 px-3 font-mono text-white">{ch.cps > 0 ? `RM${ch.cps}` : '—'}</td>
                  <td className="py-2.5 px-3 font-mono text-white">{ch.leads_sales || '—'}</td>
                  <td className="py-2.5 px-3 font-mono text-white">{ch.closed || '—'}</td>
                  <td className="py-2.5 px-3 font-mono text-white">{ch.closing_rate > 0 ? `${ch.closing_rate}%` : '—'}</td>
                  <td className="py-2.5 px-3 font-mono text-white">{ch.commission > 0 ? `RM${ch.commission.toLocaleString('en-MY', {maximumFractionDigits: 0})}` : '—'}</td>
                  <td className="py-2.5 px-3 font-mono"><span className={`${(ch.com_per_lot || 0) >= 12 ? 'text-emerald-400' : (ch.com_per_lot || 0) >= 8 ? 'text-amber-400' : 'text-red-400'}`}>{(ch.com_per_lot || 0) > 0 ? `$${ch.com_per_lot.toFixed(1)}` : '—'}</span></td>
                  <td className="py-2.5 px-3 font-mono text-white">{ch.deposit > 0 ? `$${ch.deposit.toLocaleString('en-MY', {maximumFractionDigits: 0})}` : '—'}</td>
                  <td className="py-2.5 px-3">
                    {ch.content_score > 0 ? (
                      <Badge className="text-[9px] font-bold" style={{
                        backgroundColor: ch.content_score >= 7 ? '#10b98115' : ch.content_score >= 4 ? '#f59e0b15' : '#ef444415',
                        color: ch.content_score >= 7 ? '#10b981' : ch.content_score >= 4 ? '#f59e0b' : '#ef4444',
                        border: `1px solid ${ch.content_score >= 7 ? '#10b98135' : ch.content_score >= 4 ? '#f59e0b35' : '#ef444435'}`
                      }}>{ch.content_score}/10</Badge>
                    ) : <span className="text-[9px] text-[hsl(var(--text-muted))]">—</span>}
                  </td>
                  <td className="py-2.5 px-3"><MiniSparkline data={ch.sparkline} /></td>
                  <td className="py-2.5 px-3">
                    <div className="flex items-center gap-1 relative group">
                      {ch.issues_count.critical > 0 && <Badge className="text-[8px] bg-[#ef4444]/15 text-[#ef4444] border-[#ef4444]/30 cursor-pointer" onClick={() => setSelectedIssue({ severity: 'critical', area: 'Issues', message: `${ch.issues_count.critical} critical issue(s)`, channel: ch.name, channelData: ch })}>{ch.issues_count.critical}</Badge>}
                      {ch.issues_count.warning > 0 && <Badge className="text-[8px] bg-[#f59e0b]/15 text-[#f59e0b] border-[#f59e0b]/30 cursor-pointer" onClick={() => setSelectedIssue({ severity: 'warning', area: 'Issues', message: `${ch.issues_count.warning} warning(s)`, channel: ch.name, channelData: ch })}>{ch.issues_count.warning}</Badge>}
                      {ch.issues_count.critical === 0 && ch.issues_count.warning === 0 && <CheckCircle size={12} className="text-[#10b981]" />}
                      {/* Hover tooltip */}
                      {(ch.issues_count.critical > 0 || ch.issues_count.warning > 0) && (
                        <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block z-50 w-48 p-2 rounded-lg bg-[#1a1a22] border border-[hsl(var(--border))] shadow-xl text-[10px]">
                          {ch.issues_count.critical > 0 && <div className="flex items-center gap-1 text-[#ef4444] mb-1"><AlertTriangle size={10} /> {ch.issues_count.critical} critical</div>}
                          {ch.issues_count.warning > 0 && <div className="flex items-center gap-1 text-[#f59e0b]"><AlertCircle size={10} /> {ch.issues_count.warning} warnings</div>}
                          <div className="text-[9px] text-[hsl(var(--text-muted))] mt-1">Click to see details</div>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="py-2.5 px-2">
                    <button onClick={() => navigate(`/ib/${ch.ib_id}/details`, { state: { activeTab: 'war-room' } })} className="p-1 rounded hover:bg-[hsl(var(--surface-overlay))] transition-colors" title="Open Channel War Room" data-testid={`go-channel-${ch.ib_id}`}>
                      <ChevronRight size={14} className="text-[hsl(var(--text-muted))]" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* Floating Compare Bar — appears when compare-mode on AND 2+ channels selected */}
      {compareMode && compareSet.size >= 2 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 px-4 py-3 rounded-full bg-[#1a1a25] border border-[#E63946]/40 shadow-2xl" data-testid="compare-floating-bar">
          <GitCompare size={14} className="text-[#E63946]" />
          <span className="text-xs text-white font-semibold">{compareSet.size} channels selected</span>
          <button
            onClick={() => {
              setComparePeriod(periodOptions[0]?.key);
              setShowCompare(true);
            }}
            className="px-3 py-1.5 rounded-full bg-[#E63946] text-white text-[11px] font-bold hover:bg-[#d32f3f] transition-all"
            data-testid="btn-open-compare"
          >
            Compare Selected
          </button>
          <button
            onClick={() => setCompareSet(new Set())}
            className="p-1 rounded-full hover:bg-[hsl(var(--surface-overlay))] transition-all"
            data-testid="btn-clear-compare"
            title="Clear selection"
          >
            <X size={12} className="text-[hsl(var(--text-muted))]" />
          </button>
        </div>
      )}

      {/* Side-by-Side Compare Dialog */}
      <Dialog open={showCompare} onOpenChange={setShowCompare}>
        <DialogContent className="max-w-5xl bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white max-h-[85vh] overflow-y-auto" data-testid="compare-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <DialogTitle className="flex items-center gap-2 text-base">
                <GitCompare size={16} className="text-[#E63946]" />
                Side-by-Side Channel Comparison
              </DialogTitle>
              {/* Period picker */}
              <div className="flex items-center gap-2" data-testid="compare-period-picker">
                <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))] font-bold">Period:</span>
                <select
                  value={comparePeriod || periodOptions[0]?.key || ''}
                  onChange={(e) => setComparePeriod(e.target.value)}
                  className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-md px-2 py-1 text-[11px] text-white cursor-pointer"
                  data-testid="compare-period-select"
                >
                  {periodOptions.map((opt) => (
                    <option key={opt.key} value={opt.key}>{opt.label}{opt.key === periodOptions[0]?.key ? ' (MTD)' : ''}</option>
                  ))}
                </select>
                {compareLoading && <Loader2 size={12} className="animate-spin text-[#E63946]" />}
              </div>
            </div>
          </DialogHeader>
          {(() => {
            // Use historical compareData if the selected period is not the current
            // month; otherwise fall back to the main `data` (MTD).
            const dataForCompare = (compareData && comparePeriod && comparePeriod !== periodOptions[0]?.key) ? compareData : data;
            const chansAll = (dataForCompare?.channels || []).filter((c) => c.has_data);
            const chans = Array.from(compareSet)
              .map((id) => chansAll.find((c) => c.ib_id === id) || activeChannels.find((c) => c.ib_id === id))
              .filter(Boolean);
            if (chans.length < 2) return <p className="text-[11px] text-[hsl(var(--text-muted))] mt-2">Pilih sekurang-kurangnya 2 channels untuk compare.</p>;

            // Metric rows to compare — best-of highlighting for each row
            const rows = [
              { key: 'health', label: 'Health Score', fmt: (v) => v ?? '—', higherBetter: true },
              { key: 'spend', label: 'Ad Spend', fmt: (v) => v > 0 ? `RM${Number(v).toLocaleString('en-MY',{maximumFractionDigits:0})}` : '—', higherBetter: false, note: 'lower = efficient' },
              { key: 'subs', label: 'Subscribers', fmt: (v) => v || '—', higherBetter: true },
              { key: 'cps', label: 'CPS (Cost/Sub)', fmt: (v) => v > 0 ? `RM${v}` : '—', higherBetter: false },
              { key: 'leads_sales', label: 'Leads', fmt: (v) => v || '—', higherBetter: true },
              { key: 'closed', label: 'Closed', fmt: (v) => v || '—', higherBetter: true },
              { key: 'closing_rate', label: 'Closing Rate', fmt: (v) => v > 0 ? `${v}%` : '—', higherBetter: true },
              { key: 'commission', label: 'Commission (RM)', fmt: (v) => v > 0 ? `RM${Number(v).toLocaleString('en-MY',{maximumFractionDigits:0})}` : '—', higherBetter: true },
              { key: 'com_per_lot', label: 'Com/Lot ($)', fmt: (v) => v > 0 ? `$${v.toFixed(1)}` : '—', higherBetter: true },
              { key: 'deposit', label: 'Deposit ($)', fmt: (v) => v > 0 ? `$${Number(v).toLocaleString('en-MY',{maximumFractionDigits:0})}` : '—', higherBetter: true },
              { key: 'content_score', label: 'Content Score', fmt: (v) => v > 0 ? `${v}/10` : '—', higherBetter: true },
            ];

            const winnersFor = (row) => {
              const vals = chans.map((c) => Number(c[row.key] || 0));
              const nonZero = vals.filter((v) => v > 0);
              if (nonZero.length === 0) return new Set();
              const target = row.higherBetter ? Math.max(...nonZero) : Math.min(...nonZero);
              const winners = new Set();
              vals.forEach((v, i) => { if (v === target && v > 0) winners.add(i); });
              return winners;
            };

            return (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-[11px]" data-testid="compare-table">
                  <thead>
                    <tr className="border-b border-[hsl(var(--border))]">
                      <th className="py-2 px-3 text-left text-[9px] font-bold text-[hsl(var(--text-muted))] uppercase tracking-wider w-[150px]">Metric</th>
                      {chans.map((c) => (
                        <th key={c.ib_id} className="py-2 px-3 text-center text-[10px] font-bold text-white uppercase">
                          <div className="flex flex-col items-center gap-1">
                            <span className="truncate max-w-[140px]">{c.name}</span>
                            <HealthLabel score={c.health} />
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const winners = winnersFor(row);
                      return (
                        <tr key={row.key} className="border-b border-[hsl(var(--border))]/40 hover:bg-[hsl(var(--surface-elevated))]" data-testid={`compare-row-${row.key}`}>
                          <td className="py-2 px-3 text-[10px] font-semibold text-[hsl(var(--text-muted))]">
                            {row.label}
                            {row.note && <div className="text-[9px] text-[hsl(var(--text-muted))]/70 font-normal">{row.note}</div>}
                          </td>
                          {chans.map((c, idx) => {
                            const val = c[row.key];
                            const isWinner = winners.has(idx);
                            return (
                              <td key={c.ib_id} className={`py-2 px-3 text-center font-mono ${isWinner ? 'text-[#10b981] font-bold bg-[#10b981]/5' : 'text-white'}`}>
                                {row.fmt(val)}
                                {isWinner && <span className="ml-1 text-[9px]">★</span>}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                    {/* Issues row */}
                    <tr className="border-b border-[hsl(var(--border))]/40">
                      <td className="py-2 px-3 text-[10px] font-semibold text-[hsl(var(--text-muted))]">Issues</td>
                      {chans.map((c) => (
                        <td key={c.ib_id} className="py-2 px-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {c.issues_count.critical > 0 && <Badge className="text-[8px] bg-[#ef4444]/15 text-[#ef4444] border-[#ef4444]/30">{c.issues_count.critical}C</Badge>}
                            {c.issues_count.warning > 0 && <Badge className="text-[8px] bg-[#f59e0b]/15 text-[#f59e0b] border-[#f59e0b]/30">{c.issues_count.warning}W</Badge>}
                            {c.issues_count.critical === 0 && c.issues_count.warning === 0 && <CheckCircle size={11} className="text-[#10b981]" />}
                          </div>
                        </td>
                      ))}
                    </tr>
                    {/* Trend row */}
                    <tr className="border-b border-[hsl(var(--border))]/40">
                      <td className="py-2 px-3 text-[10px] font-semibold text-[hsl(var(--text-muted))]">7d Trend</td>
                      {chans.map((c) => (
                        <td key={c.ib_id} className="py-2 px-3 text-center">
                          <div className="flex justify-center"><MiniSparkline data={c.sparkline} /></div>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>

                {/* Best-of summary card */}
                <div className="mt-4 p-3 rounded-lg border border-[#10b981]/25 bg-[#10b981]/5">
                  <p className="text-[10px] font-bold uppercase text-[#10b981] mb-1">Best performer per metric</p>
                  <p className="text-[11px] text-white/80">
                    Cell yang ditandakan <span className="text-[#10b981] font-bold">hijau ★</span> menunjukkan channel terbaik untuk metric tersebut. Untuk metrik cost (spend, CPS), lower is better.
                  </p>
                </div>

                {/* Deep-dive links */}
                <div className="mt-4 flex flex-wrap gap-2">
                  {chans.map((c) => (
                    <button
                      key={c.ib_id}
                      onClick={() => { setShowCompare(false); setSelectedIssue(null); navigate(`/ib/${c.ib_id}/details`, { state: { activeTab: 'war-room' } }); }}
                      className="px-3 py-1.5 rounded-lg bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] hover:border-[#E63946]/50 text-white text-[11px] transition-all"
                      data-testid={`compare-open-${c.ib_id}`}
                    >
                      Open {c.name} <ChevronRight size={11} className="inline-block ml-1" />
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Last Updated */}
      <div className="text-center py-3">
        <span className="text-[10px] text-[hsl(var(--text-muted))]">Last updated: {new Date().toLocaleString('en-MY', { dateStyle: 'medium', timeStyle: 'short' })} — MTD Day {data.day} of {data.month}</span>
      </div>

      {/* Issue/Channel Detail Popup */}
      <Dialog open={!!selectedIssue} onOpenChange={() => setSelectedIssue(null)}>
        <DialogContent className="sm:max-w-lg bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              {selectedIssue?.severity === 'critical' && <AlertTriangle size={16} className="text-[#ef4444]" />}
              {selectedIssue?.severity === 'warning' && <AlertCircle size={16} className="text-[#f59e0b]" />}
              {selectedIssue?.channel}
            </DialogTitle>
          </DialogHeader>
          {selectedIssue && (() => {
            const channelIssues = all_issues.filter(i => i.channel === selectedIssue.channel);
            const critIssues = channelIssues.filter(i => i.severity === 'critical');
            const warnIssues = channelIssues.filter(i => i.severity === 'warning');
            const infoIssues = channelIssues.filter(i => i.severity === 'info');
            return (
              <div className="space-y-4 mt-2">
                {/* All issues for this channel */}
                {channelIssues.length > 0 ? (
                  <div>
                    <p className="text-[10px] font-bold uppercase text-[hsl(var(--text-muted))] mb-2">Detected Issues ({channelIssues.length})</p>
                    <div className="space-y-1.5 max-h-[250px] overflow-y-auto">
                      {critIssues.map((iss, i) => (
                        <div key={`c-${i}`} className="flex items-start gap-2.5 p-2.5 rounded-lg border border-[#ef4444]/30 bg-[#ef4444]/5">
                          <AlertTriangle size={12} className="text-[#ef4444] mt-0.5 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <span className="text-[10px] font-bold text-[#ef4444] uppercase">{iss.area}</span>
                            <p className="text-[11px] text-white/90 mt-0.5">{iss.message}</p>
                          </div>
                        </div>
                      ))}
                      {warnIssues.map((iss, i) => (
                        <div key={`w-${i}`} className="flex items-start gap-2.5 p-2.5 rounded-lg border border-[#f59e0b]/30 bg-[#f59e0b]/5">
                          <AlertCircle size={12} className="text-[#f59e0b] mt-0.5 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <span className="text-[10px] font-bold text-[#f59e0b] uppercase">{iss.area}</span>
                            <p className="text-[11px] text-white/90 mt-0.5">{iss.message}</p>
                          </div>
                        </div>
                      ))}
                      {infoIssues.map((iss, i) => (
                        <div key={`i-${i}`} className="flex items-start gap-2.5 p-2.5 rounded-lg border border-[#3b82f6]/30 bg-[#3b82f6]/5">
                          <AlertCircle size={12} className="text-[#3b82f6] mt-0.5 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <span className="text-[10px] font-bold text-[#3b82f6] uppercase">{iss.area}</span>
                            <p className="text-[11px] text-white/90 mt-0.5">{iss.message}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="p-3 rounded-lg border border-[#10b981]/30 bg-[#10b981]/5 flex items-center gap-2">
                    <CheckCircle size={14} className="text-[#10b981]" />
                    <span className="text-[11px] text-[#10b981]">Tiada isu dikesan untuk channel ini</span>
                  </div>
                )}

                {/* Channel quick stats */}
                {selectedIssue.channelData && (
                  <div>
                    <p className="text-[10px] font-bold uppercase text-[hsl(var(--text-muted))] mb-2">Channel Summary</p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { label: 'Health', val: selectedIssue.channelData.health },
                        { label: 'Subs', val: selectedIssue.channelData.subs },
                        { label: 'Leads', val: selectedIssue.channelData.leads_sales },
                        { label: 'Closed', val: selectedIssue.channelData.closed },
                        { label: 'Commission', val: selectedIssue.channelData.commission > 0 ? `RM${selectedIssue.channelData.commission.toLocaleString('en-MY', {maximumFractionDigits:0})}` : '0' },
                        { label: 'Deposit', val: selectedIssue.channelData.deposit > 0 ? `$${selectedIssue.channelData.deposit.toLocaleString('en-MY', {maximumFractionDigits:0})}` : '0' },
                      ].map(s => (
                        <div key={s.label} className="p-2 rounded-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))] text-center">
                          <div className="text-sm font-bold font-mono text-white">{s.val}</div>
                          <div className="text-[9px] text-[hsl(var(--text-muted))]">{s.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Navigate button */}
                {selectedIssue.channelData && (
                  <button
                    onClick={() => { setSelectedIssue(null); navigate(`/ib/${selectedIssue.channelData.ib_id}/details`, { state: { activeTab: 'war-room' } }); }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#E63946] text-white text-xs font-semibold hover:bg-[#d32f3f] transition-all"
                    data-testid="go-to-channel-warroom"
                  >
                    Open Channel War Room <ChevronRight size={14} />
                  </button>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
